import { deepgramAccessToken, forgetDeepgramToken } from '../../../src/services/deepgramToken';
import { ensureLiveSession } from '../../../src/services/liveSessions';
import { CHANNELS } from '../../../src/ipc/channels';
import { getTranscriptTerms } from '../../../src/services/settings';
import { t } from '../../../src/services/language';
import type {
  LiveSource,
  LiveTranscriptEvent,
  LiveTranscriptPayload,
  TranscribeLanguage,
} from '../../../src/shared/types';
import type { PageWebContents } from './mainWindow';
import { WEB_CONFIG } from '../config';
import { interval } from '../ticker';

/**
 * `deepgramLive.ts`'nin web karşılığı: aynı Deepgram canlı döküm akışı, Node
 * `ws` yerine tarayıcının WebSocket'i. Tarayıcı başlık ekleyemediği için kısa
 * ömürlü anahtar alt protokolde gidiyor (`['bearer', anahtar]`).
 *
 * Masaüstünde anahtar ana süreçte kalıyordu; web'de ister istemez sayfada.
 * Ömrü 60 sn ve sunucu yalnızca açık canlı oturum varken, kota/onay
 * kapılarından geçerek veriyor (bkz. web/app/api/v1/transcription/token).
 */

const DEEPGRAM_URL = 'wss://api.deepgram.com/v1/listen';
const KEEPALIVE_MS = 4000;
const CLOSE_WAIT_MS = 4000;
const MAX_QUEUED_BYTES = 2 * 1024 * 1024;

interface Connection {
  ws: WebSocket;
  webContents: PageWebContents;
  sessionId: string;
  source: LiveSource;
  queue: ArrayBuffer[];
  queuedBytes: number;
  lastSentAt: number;
  stopKeepAlive: () => void;
  closing: boolean;
  opened: boolean;
}

const connections = new Map<string, Connection>();

function connectionKey(sessionId: string, source: LiveSource): string {
  return `${sessionId}:${source}`;
}

function buildUrl(language: TranscribeLanguage, terms: string[]): string {
  const params = new URLSearchParams({
    model: WEB_CONFIG.deepgramModel,
    language: language === 'auto' ? 'multi' : language,
    smart_format: 'true',
    punctuate: 'true',
    interim_results: 'true',
    endpointing: '300',
    utterance_end_ms: '1000',
    vad_events: 'true',
    mip_opt_out: 'true',
  });
  for (const term of terms) params.append('keyterm', term);
  return `${DEEPGRAM_URL}?${params.toString()}`;
}

function emit(conn: Connection, payload: LiveTranscriptPayload): void {
  const event: LiveTranscriptEvent = { sessionId: conn.sessionId, source: conn.source, ...payload };
  conn.webContents.send(CHANNELS.liveEvent, event);
}

function flushQueue(conn: Connection): void {
  if (conn.ws.readyState !== WebSocket.OPEN) return;
  for (const chunk of conn.queue) conn.ws.send(chunk);
  conn.queue = [];
  conn.queuedBytes = 0;
  conn.lastSentAt = Date.now();
}

interface DeepgramMessage {
  type?: string;
  is_final?: boolean;
  speech_final?: boolean;
  start?: number;
  channel?: { alternatives?: Array<{ transcript?: string }> };
}

function handleMessage(conn: Connection, raw: unknown): void {
  if (typeof raw !== 'string') return;
  let message: DeepgramMessage;
  try {
    message = JSON.parse(raw) as DeepgramMessage;
  } catch {
    return;
  }
  if (message.type === 'Results') {
    const text = message.channel?.alternatives?.[0]?.transcript?.trim() ?? '';
    const isFinal = Boolean(message.is_final);
    if (!text && !isFinal) return;
    emit(conn, { kind: 'transcript', text, isFinal, speechFinal: Boolean(message.speech_final), start: message.start ?? 0 });
  } else if (message.type === 'UtteranceEnd') {
    emit(conn, { kind: 'utteranceEnd' });
  }
}

export async function openLiveSource(
  webContents: PageWebContents,
  sessionId: string,
  source: LiveSource,
  language: TranscribeLanguage
): Promise<void> {
  const key = connectionKey(sessionId, source);
  const existing = connections.get(key);
  if (existing) void closeConnection(existing, false);

  const terms = await getTranscriptTerms();
  let accessToken: string;
  try {
    const liveSessionId = await ensureLiveSession();
    accessToken = await deepgramAccessToken(liveSessionId);
  } catch (err) {
    const message = t(`Canlı döküm başlatılamadı: ${(err as Error).message}`, `Could not start live transcription: ${(err as Error).message}`);
    console.error('[deepgram]', message);
    webContents.send(CHANNELS.liveEvent, { sessionId, source, kind: 'error', message } satisfies LiveTranscriptEvent);
    return;
  }

  const ws = new WebSocket(buildUrl(language, terms), ['bearer', accessToken]);
  ws.binaryType = 'arraybuffer';

  const conn: Connection = {
    ws,
    webContents,
    sessionId,
    source,
    queue: [],
    queuedBytes: 0,
    lastSentAt: Date.now(),
    closing: false,
    opened: false,
    // Arka plandaki sekmede setInterval dakikada bire kadar kısılabiliyor;
    // duraklatmada Deepgram 10 sn sessizlikte bağlantıyı kapatır. Zamanlayıcı
    // bu yüzden işçide (ticker.ts).
    stopKeepAlive: interval(1000, () => {
      if (ws.readyState === WebSocket.OPEN && Date.now() - conn.lastSentAt >= KEEPALIVE_MS) {
        ws.send(JSON.stringify({ type: 'KeepAlive' }));
        conn.lastSentAt = Date.now();
      }
    }),
  };
  connections.set(key, conn);

  ws.onopen = () => {
    conn.opened = true;
    flushQueue(conn);
  };
  ws.onmessage = (event) => handleMessage(conn, event.data);
  ws.onerror = () => {
    console.error('[deepgram] bağlantı hatası');
  };
  ws.onclose = (event) => {
    conn.stopKeepAlive();
    if (connections.get(key) === conn) connections.delete(key);
    if (conn.closing) return;
    // Tarayıcı el sıkışma hatasının HTTP durumunu göstermiyor: hiç açılamadıysa
    // büyük olasılıkla anahtar reddedildi — yeniden bağlanmak aynı hatayı verir.
    if (!conn.opened) {
      forgetDeepgramToken();
      conn.closing = true;
      emit(conn, {
        kind: 'error',
        message: t(
          'Döküm servisi bağlantıyı kabul etmedi, kaydı yeniden başlatmayı dene.',
          'The transcription service did not accept the connection — try restarting the recording.'
        ),
      });
      return;
    }
    if (event.code === 1008 || event.code === 4001) forgetDeepgramToken();
    emit(conn, { kind: 'closed' });
  };
}

export function pushLiveAudio(sessionId: string, source: LiveSource, data: ArrayBuffer): void {
  const conn = connections.get(connectionKey(sessionId, source));
  if (!conn || conn.closing || !(data instanceof ArrayBuffer) || data.byteLength === 0) return;
  if (conn.ws.readyState === WebSocket.OPEN) {
    conn.ws.send(data);
    conn.lastSentAt = Date.now();
  } else if (conn.ws.readyState === WebSocket.CONNECTING) {
    if (conn.queuedBytes + data.byteLength > MAX_QUEUED_BYTES) return;
    conn.queue.push(data);
    conn.queuedBytes += data.byteLength;
  }
}

function closeConnection(conn: Connection, graceful: boolean): Promise<void> {
  conn.closing = true;
  const { ws } = conn;
  if (ws.readyState === WebSocket.CLOSED) return Promise.resolve();
  if (!graceful || ws.readyState !== WebSocket.OPEN) {
    ws.close();
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      ws.close();
      resolve();
    }, CLOSE_WAIT_MS);
    ws.addEventListener('close', () => {
      clearTimeout(timeout);
      resolve();
    });
    ws.send(JSON.stringify({ type: 'CloseStream' }));
  });
}

export function openLiveStreamCount(): number {
  let open = 0;
  for (const conn of connections.values()) if (!conn.closing && conn.ws.readyState === WebSocket.OPEN) open++;
  return open;
}

export async function closeAllLiveSources(message: string): Promise<void> {
  const open = [...connections.values()];
  for (const conn of open) {
    conn.closing = true;
    emit(conn, { kind: 'error', message });
  }
  await Promise.all(open.map((conn) => closeConnection(conn, true)));
}

/** Açık bütün canlı bağlantılara bilgi notu düşer (bağlantılar açık kalır). */
export function noticeAllLiveSources(message: string): void {
  for (const conn of connections.values()) emit(conn, { kind: 'notice', message });
}

export async function closeLiveSource(sessionId: string, source: LiveSource): Promise<void> {
  const conn = connections.get(connectionKey(sessionId, source));
  if (conn) await closeConnection(conn, true);
}
